import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  createProductGroup,
  listItemCategories,
  listMetalGroups,
  listProductGroups,
  showProductGroup,
  updateProductGroup,
} from "../erpClient";
import { getActiveItems } from "../utils/statusUtils";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

import { createErpSocket } from "../utils/socketIo";
import OpeningPoolModal from "../components/OpeningPoolModal";
import { getSessionBranchId } from "../utils/sessionBranch";
import { numPool } from "../utils/productGroupPool";
import {
  ERP_PRODUCT_GROUP_POOL_INVALIDATE_EVENT,
  isSocketForCurrentBranch,
  normalizeProductGroupPoolUpdatedPayload,
  normalizeTagCancelledPayload,
} from "../utils/socketPoolEvents";
import { parseApiPagination, extractListRowsFromResponse } from "../utils/parseApiPagination";

const MODAL_SEARCH_MIN_CHARS = 3;

function mapMetalGroupRows(rows) {
  return getActiveItems(
    rows.map((metal) => ({
      id: String(metal.id),
      groupName: metal.group_name || "N/A",
      shortCode: Array.isArray(metal.short_code)
        ? metal.short_code.join(", ")
        : metal.short_code || "",
      status: metal.status,
    })),
    { activeValue: 1 },
  );
}

function mapItemCategoryRows(rows) {
  return getActiveItems(
    rows.map((item) => ({
      id: String(item.id),
      categoryName: item.name || "N/A",
      shortCode: Array.isArray(item.short_code)
        ? item.short_code.join(", ")
        : item.short_code || "",
      status: item.status,
    })),
    { activeValue: 1 },
  );
}

function mapProductGroupShowToForm(pg) {
  return {
    id: pg.id,
    metalGroupId: pg.metal_group_id,
    itemCategoryId: pg.item_category_id,
    metalGroup: pg.metal_group_name || "",
    itemCategory: pg.item_category_name || "",
    productGroup: pg.name || "N/A",
    short_code: pg.short_code || "",
    created_at: pg.created_at || "",
    pool_pcs: numPool(pg.pool_pcs),
    pool_nwt: numPool(pg.pool_nwt),
  };
}

/** Prefer names from product group API; fall back to id lookup when missing. */
function pickProductGroupDisplayName(rawName, id, lookupById) {
  if (rawName != null && String(rawName).trim() !== "") {
    return String(rawName).trim();
  }
  return typeof lookupById === "function" ? lookupById(id) : "N/A";
}

function ProductGroupTable() {
  // const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const [allProductGroups, setAllProductGroups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showOpeningPoolModal, setShowOpeningPoolModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [addMetalOptions, setAddMetalOptions] = useState([]);
  const [addItemOptions, setAddItemOptions] = useState([]);
  const [editMetalOptions, setEditMetalOptions] = useState([]);
  const [editItemOptions, setEditItemOptions] = useState([]);
  const [metalOptionsLoading, setMetalOptionsLoading] = useState(false);
  const [itemOptionsLoading, setItemOptionsLoading] = useState(false);
  const lastAddMetalSearchRef = useRef("");
  const lastAddItemSearchRef = useRef("");
  const lastEditMetalSearchRef = useRef("");
  const lastEditItemSearchRef = useRef("");
  const [isLoading, setIsLoading] = useState(true);

  const radius = buttonTableStyle === "curve" ? 8 : 0;
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 48,
      borderRadius: radius,
      borderColor: state.isFocused ? "var(--theme-primary)" : "#ced4da",
      boxShadow: "none",
      "&:hover": {
        borderColor: "var(--theme-primary)",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0.375rem 0.75rem",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: radius,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 220,
    }),
  };

  // New Add Modal Form State
  const [newMetalGroup, setNewMetalGroup] = useState(null);
  const [newItemCategory, setNewItemCategory] = useState(null);
  const [newProductName, setNewProductName] = useState("");
  const [newShortCode, setNewShortCode] = useState("");

  const getMetalGroupNameById = useCallback(() => "N/A", []);

  const getItemCategoryNameById = useCallback(() => "N/A", []);

  const fetchProductGroups = useCallback(
    async (page = 1, limit = 10, search = "") => {
      setIsLoading(true);
      try {
        const response = await listProductGroups({
          branch_id: getSessionBranchId(),
          search: search.trim(),
          page,
          limit,
        });
        const rows = extractListRowsFromResponse(response, ["product_groups"]);

        const mapped = rows.map((pg) => {
          return {
            id: pg.id,
            metalGroupId: pg.metal_group_id,
            itemCategoryId: pg.item_category_id,
            metalGroup: pickProductGroupDisplayName(
              pg.metal_group_name,
              pg.metal_group_id,
              getMetalGroupNameById,
            ),
            itemCategory: pickProductGroupDisplayName(
              pg.item_category_name,
              pg.item_category_id,
              getItemCategoryNameById,
            ),
            productGroup: pg.name || "N/A",
            short_code: pg.short_code || "",
            created_at: pg.created_at || "",
            pool_pcs: numPool(pg.pool_pcs),
            pool_nwt: numPool(pg.pool_nwt),
          };
        });

        mapped.sort((a, b) => Number(b.id) - Number(a.id));
        setAllProductGroups(mapped);
        setTotalCount(parseApiPagination(response, mapped.length).total);
      } catch (error) {
        console.error("Error fetching product groups:", error);
        toast.error(
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch product groups",
        );
        setAllProductGroups([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [getItemCategoryNameById, getMetalGroupNameById],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchProductGroups(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchProductGroups]);

  const updateProductGroups = async (updatedProductGroup) => {
    try {
      const response = await updateProductGroup({
        id: Number(updatedProductGroup.id),
        metal_group_id: Number(updatedProductGroup.metalGroupId),
        item_category_id: Number(updatedProductGroup.itemCategoryId),
        name: updatedProductGroup.productGroup,
        short_code: updatedProductGroup.short_code,
      });

      if (response?.success) {
        toast.success("Product group updated successfully");
        const data = response.data?.product_group || response.data || {};
        const merged = {
          id: data.id ?? updatedProductGroup.id,
          metalGroupId: data.metal_group_id ?? updatedProductGroup.metalGroupId,
          itemCategoryId:
            data.item_category_id ?? updatedProductGroup.itemCategoryId,
          metalGroup: pickProductGroupDisplayName(
            data.metal_group_name,
            data.metal_group_id ?? updatedProductGroup.metalGroupId,
            getMetalGroupNameById,
          ),
          itemCategory: pickProductGroupDisplayName(
            data.item_category_name,
            data.item_category_id ?? updatedProductGroup.itemCategoryId,
            getItemCategoryNameById,
          ),
          productGroup: data.name ?? updatedProductGroup.productGroup ?? "N/A",
          short_code: data.short_code ?? updatedProductGroup.short_code ?? "",
          created_at: data.created_at ?? updatedProductGroup.created_at ?? "",
          pool_pcs: numPool(
            data.pool_pcs ?? updatedProductGroup.pool_pcs,
          ),
          pool_nwt: numPool(
            data.pool_nwt ?? updatedProductGroup.pool_nwt,
          ),
        };
        setAllProductGroups((prev) =>
          prev
            .map((p) =>
              String(p.id) === String(merged.id) ? { ...p, ...merged } : p,
            )
            .sort((a, b) => Number(b.id) - Number(a.id)),
        );
      } else {
        toast.error(response?.message || "Failed to update product group");
      }
    } catch (error) {
      console.error("Error updating product group:", error);
      toast.error(
        error.response?.data?.message || "Failed to update product group",
      );
    }
  };

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in ProductGroupTable, refetching product groups...",
        event.detail,
      );
      setCurrentPage(1);
      fetchProductGroups(1, itemsPerPage, searchQuery);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchProductGroups]);

  const modalSearchNoOptionsMessage = (loading, inputLength) => {
    if (loading) return "Loading...";
    if (inputLength < MODAL_SEARCH_MIN_CHARS) {
      return `Type ${MODAL_SEARCH_MIN_CHARS}+ characters to search`;
    }
    return "No options found";
  };

  const searchMetalGroups = async (search, { target = "add" } = {}) => {
    const trimmed = String(search || "").trim();
    const lastRef = target === "edit" ? lastEditMetalSearchRef : lastAddMetalSearchRef;
    const setOptions = target === "edit" ? setEditMetalOptions : setAddMetalOptions;

    if (trimmed.length < MODAL_SEARCH_MIN_CHARS) {
      lastRef.current = "";
      setOptions([]);
      return;
    }
    if (trimmed === lastRef.current) return;

    setMetalOptionsLoading(true);
    try {
      const res = await listMetalGroups({ search: trimmed });
      const rows = extractListRowsFromResponse(res, ["metal_groups", "records"]);
      const mapped = mapMetalGroupRows(rows);
      lastRef.current = trimmed;
      setOptions(mapped);
    } catch (error) {
      console.error("Error searching metal groups:", error);
      toast.error(
        error?.response?.data?.message || "Failed to search metal groups",
      );
      setOptions([]);
    } finally {
      setMetalOptionsLoading(false);
    }
  };

  const searchItemCategories = async (search, { target = "add" } = {}) => {
    const trimmed = String(search || "").trim();
    const lastRef = target === "edit" ? lastEditItemSearchRef : lastAddItemSearchRef;
    const setOptions = target === "edit" ? setEditItemOptions : setAddItemOptions;

    if (trimmed.length < MODAL_SEARCH_MIN_CHARS) {
      lastRef.current = "";
      setOptions([]);
      return;
    }
    if (trimmed === lastRef.current) return;

    setItemOptionsLoading(true);
    try {
      const res = await listItemCategories({ search: trimmed });
      const rows = extractListRowsFromResponse(res, [
        "item_categories",
        "categories",
      ]);
      const mapped = mapItemCategoryRows(rows);
      lastRef.current = trimmed;
      setOptions(mapped);
    } catch (error) {
      console.error("Error searching item categories:", error);
      toast.error(
        error?.response?.data?.message || "Failed to search item categories",
      );
      setOptions([]);
    } finally {
      setItemOptionsLoading(false);
    }
  };

  const loadProductGroupForEdit = async (productGroupId) => {
    setEditLoading(true);
    setSelectedProduct(null);
    try {
      const res = await showProductGroup({
        id: Number(productGroupId),
        branch_id: getSessionBranchId(),
      });
      const pg = res?.data?.product_group ?? res?.data;
      if (!res?.success || !pg) {
        toast.error(res?.message || "Failed to load product group");
        setShowEditModal(false);
        return;
      }
      setSelectedProduct(mapProductGroupShowToForm(pg));
      setEditMetalOptions([]);
      setEditItemOptions([]);
      lastEditMetalSearchRef.current = "";
      lastEditItemSearchRef.current = "";
    } catch (error) {
      console.error("Error loading product group:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load product group",
      );
      setShowEditModal(false);
    } finally {
      setEditLoading(false);
    }
  };

  const getEditMetalSelectValue = (product) => {
    if (!product?.metalGroupId) return null;
    const found = editMetalOptions.find(
      (metal) => String(metal.id) === String(product.metalGroupId),
    );
    if (found) return found;
    return {
      id: String(product.metalGroupId),
      groupName: product.metalGroup || String(product.metalGroupId),
      shortCode: "",
      status: 1,
    };
  };

  const getEditItemCategorySelectValue = (product) => {
    if (!product?.itemCategoryId) return null;
    const found = editItemOptions.find(
      (item) => String(item.id) === String(product.itemCategoryId),
    );
    if (found) return found;
    return {
      id: String(product.itemCategoryId),
      categoryName: product.itemCategory || String(product.itemCategoryId),
      shortCode: "",
      status: 1,
    };
  };

  const resetAddModalSearchState = () => {
    setAddMetalOptions([]);
    setAddItemOptions([]);
    lastAddMetalSearchRef.current = "";
    lastAddItemSearchRef.current = "";
  };

  const resetEditModalSearchState = () => {
    setEditMetalOptions([]);
    setEditItemOptions([]);
    lastEditMetalSearchRef.current = "";
    lastEditItemSearchRef.current = "";
  };

  // Handle Edit Click
  const handleEditClick = (product) => {
    resetEditModalSearchState();
    setShowEditModal(true);
    void loadProductGroupForEdit(product.id);
  };

  // Handle Save Changes
  const handleSaveChanges = () => {
    updateProductGroups(selectedProduct);
    setShowEditModal(false);
    setSelectedProduct(null);
    resetEditModalSearchState();
  };

  // Socket.IO listener for real-time product group updates
  useEffect(() => {
    const socket = createErpSocket();

    // Connection event logging
    socket.on("connect", () => { });

    socket.on("disconnect", (reason) => { });

    socket.on("connect_error", (error) => {
      console.error("ProductGroupTable: Socket connection error:", error);
    });

    const upsertFromSocket = (data) => {
      const pg = data?.product_group;
      if (!pg) return;
      const next = {
        id: pg.id,
        metalGroupId: pg.metal_group_id,
        itemCategoryId: pg.item_category_id,
        metalGroup: pickProductGroupDisplayName(
          pg.metal_group_name,
          pg.metal_group_id,
          () => "N/A",
        ),
        itemCategory: pickProductGroupDisplayName(
          pg.item_category_name,
          pg.item_category_id,
          () => "N/A",
        ),
        productGroup: pg.name || "N/A",
        short_code: pg.short_code || "",
        created_at: pg.created_at || "",
        pool_pcs: numPool(pg.pool_pcs),
        pool_nwt: numPool(pg.pool_nwt),
      };
      setAllProductGroups((prev) => {
        const exists = prev.some((p) => String(p.id) === String(next.id));
        const hasMetalName =
          pg.metal_group_name != null &&
          String(pg.metal_group_name).trim() !== "";
        const hasItemName =
          pg.item_category_name != null &&
          String(pg.item_category_name).trim() !== "";
        const hasPoolPcs = pg.pool_pcs != null && pg.pool_pcs !== "";
        const hasPoolNwt = pg.pool_nwt != null && pg.pool_nwt !== "";
        const merged = exists
          ? prev.map((p) => {
            if (String(p.id) !== String(next.id)) return p;
            const row = { ...p, ...next };
            if (!hasMetalName) row.metalGroup = p.metalGroup;
            if (!hasItemName) row.itemCategory = p.itemCategory;
            if (!hasPoolPcs) row.pool_pcs = p.pool_pcs;
            if (!hasPoolNwt) row.pool_nwt = p.pool_nwt;
            return row;
          })
          : [next, ...prev];
        return merged.sort((a, b) => Number(b.id) - Number(a.id));
      });
    };

    socket.on("product_group_created", upsertFromSocket);
    socket.on("product_group_updated", upsertFromSocket);

    const handleMetalTransferCreated = (payload) => {
      if (!isSocketForCurrentBranch(payload)) return;
      fetchProductGroups(currentPage, itemsPerPage, searchQuery);
    };

    const handleTagCancelled = (payload) => {
      if (!isSocketForCurrentBranch(payload)) return;
      const cancel = normalizeTagCancelledPayload(payload);
      if (!cancel.product_group_id || !cancel.pool) return;
      setAllProductGroups((prev) =>
        prev.map((pg) =>
          String(pg.id) === String(cancel.product_group_id)
            ? {
              ...pg,
              pool_nwt: numPool(cancel.pool.pool_nwt),
              pool_pcs: numPool(cancel.pool.pool_pcs),
            }
            : pg,
        ),
      );
    };

    const handleProductGroupPoolUpdated = (payload) => {
      if (!isSocketForCurrentBranch(payload)) return;
      const pools = normalizeProductGroupPoolUpdatedPayload(payload);
      if (pools.length === 0) return;

      const poolByGroupId = new Map(
        pools.map((pool) => [
          String(pool.product_group_id),
          {
            pool_nwt: numPool(pool.pool_nwt),
            pool_pcs: numPool(pool.pool_pcs),
          },
        ]),
      );

      setAllProductGroups((prev) =>
        prev.map((pg) => {
          const nextPool = poolByGroupId.get(String(pg.id));
          if (!nextPool) return pg;
          return {
            ...pg,
            pool_nwt: nextPool.pool_nwt,
            pool_pcs: nextPool.pool_pcs,
          };
        }),
      );
    };

    socket.on("metal_transfer_created", handleMetalTransferCreated);
    socket.on("tag_cancelled", handleTagCancelled);
    socket.on("product_group_pool_updated", handleProductGroupPoolUpdated);

    const onPoolInvalidate = () => {
      fetchProductGroups(currentPage, itemsPerPage, searchQuery);
    };
    window.addEventListener(
      ERP_PRODUCT_GROUP_POOL_INVALIDATE_EVENT,
      onPoolInvalidate,
    );

    return () => {
      socket.off("product_group_updated", upsertFromSocket);
      socket.off("product_group_created", upsertFromSocket);
      socket.off("metal_transfer_created", handleMetalTransferCreated);
      socket.off("tag_cancelled", handleTagCancelled);
      socket.off("product_group_pool_updated", handleProductGroupPoolUpdated);
      window.removeEventListener(
        ERP_PRODUCT_GROUP_POOL_INVALIDATE_EVENT,
        onPoolInvalidate,
      );
      socket.disconnect();
    };
  }, [currentPage, itemsPerPage, searchQuery, fetchProductGroups]);

  const displayedProducts = allProductGroups;

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Function to generate suggested product group name
  const generateSuggestedProductGroupName = (metalGroup, itemCategory) => {
    if (metalGroup && itemCategory) {
      return `${metalGroup.groupName} ${itemCategory.categoryName} Group`;
    }
    return "";
  };

  // Function to generate short code based on product group name
  const generateShortCode = (productGroup) => {
    return productGroup
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase()) // Get the first letter of each word
      .join(""); // Join them together without any dashes
  };

  // Function to generate short code for add form
  const generateShortCodeForAdd = (name) => {
    return name
      .split(" ")
      .filter((word) => word && !word.includes("-"))
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  // Generate suggested name for add form
  const generateSuggestedName = useCallback(() => {
    if (newMetalGroup && newItemCategory) {
      return `${newMetalGroup.originalName} - ${newItemCategory.originalName} Group`;
    }
    return "";
  }, [newMetalGroup, newItemCategory]);

  // Update suggestion and short code when selections change
  useEffect(() => {
    const name = generateSuggestedName();
    setNewProductName(name);
    setNewShortCode(generateShortCodeForAdd(name));
  }, [generateSuggestedName, newMetalGroup, newItemCategory]);

  // Update short code when product name changes
  useEffect(() => {
    setNewShortCode(generateShortCodeForAdd(newProductName));
  }, [newProductName]);

  // Handle Add Product Group submit
  const handleAddProductGroup = async (e) => {
    e.preventDefault();

    try {
      const response = await createProductGroup({
        metal_group_id: Number(newMetalGroup?.value),
        item_category_id: Number(newItemCategory?.value),
        name: newProductName,
        short_code: newShortCode,
      });
      if (response?.success) {
        toast.success("Product group added successfully");
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 2000);

        // Reset form
        setNewMetalGroup(null);
        setNewItemCategory(null);
        setNewProductName("");
        setNewShortCode("");

        // Close modal
        setShowAddModal(false);
        resetAddModalSearchState();

        const data = response.data?.product_group || response.data || {};
        const next = {
          id: data.id,
          metalGroupId: data.metal_group_id ?? newMetalGroup?.value,
          itemCategoryId: data.item_category_id ?? newItemCategory?.value,
          metalGroup: pickProductGroupDisplayName(
            data.metal_group_name,
            data.metal_group_id ?? newMetalGroup?.value,
            getMetalGroupNameById,
          ),
          itemCategory: pickProductGroupDisplayName(
            data.item_category_name,
            data.item_category_id ?? newItemCategory?.value,
            getItemCategoryNameById,
          ),
          productGroup: data.name ?? newProductName ?? "N/A",
          short_code: data.short_code ?? newShortCode ?? "",
          created_at: data.created_at ?? "",
          pool_pcs: numPool(data.pool_pcs),
          pool_nwt: numPool(data.pool_nwt),
        };
        if (next.id !== undefined && next.id !== null) {
          setAllProductGroups((prev) => {
            const exists = prev.some((p) => String(p.id) === String(next.id));
            const merged = exists
              ? prev.map((p) =>
                String(p.id) === String(next.id) ? { ...p, ...next } : p,
              )
              : [next, ...prev];
            return merged.sort((a, b) => Number(b.id) - Number(a.id));
          });
        } else {
          await fetchProductGroups(currentPage, itemsPerPage, searchQuery);
        }
      } else {
        toast.error(response?.message || "Failed to add product group");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message || "Failed to add product group",
      );
    }
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Product Group Table</h4>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn btn-info ${buttonClass}`}
                onClick={() => setShowOpeningPoolModal(true)}
              >
                Opening Pool
              </button>
              <button
                type="button"
                className={`btn btn-success ${buttonClass}`}
                onClick={() => setShowAddModal(true)}
              >
                Add Product Group
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Metal Group</th>
                    <th>Item Category</th>
                    <th>Product Group</th>
                    <th>Balance</th>
                    <th>Short Code</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedProducts.length > 0 ? (
                    displayedProducts.map((product) => (
                      <tr key={product.id}>
                        <td>
                          {product.metalGroup ??
                            getMetalGroupNameById(product.metalGroupId)}
                        </td>
                        <td>
                          {product.itemCategory ??
                            getItemCategoryNameById(product.itemCategoryId)}
                        </td>
                        <td>{product.productGroup}</td>
                        <td>
                          <div>
                            <span
                              className="badge bg-light text-dark"
                              style={{ fontSize: "0.85rem" }}
                            >
                              Pcs: {numPool(product.pool_pcs)}
                            </span>
                          </div>
                          <div className="mt-1">
                            <span
                              className="badge bg-light text-dark"
                              style={{ fontSize: "0.85rem" }}
                            >
                              NWt: {numPool(product.pool_nwt).toFixed(2)}
                            </span>
                          </div>
                        </td>
                        <td>{product.short_code || "-"}</td>
                        <td>
                          <button
                            className={`btn btn-primary btn-sm ${buttonClass}`}
                            onClick={() => handleEditClick(product)}
                          >
                            <Icon
                              icon="solar:pen-bold"
                              width="16"
                              height="16"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No product groups found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Edit Product Group Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedProduct(null);
          resetEditModalSearchState();
        }}
        centered
        contentClassName={cardClass}
      // size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Product Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLoading ? (
            <div className="text-center py-4">Loading product group...</div>
          ) : selectedProduct ? (
            <Form>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label>Metal Group</Form.Label>
                  <Select
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    options={editMetalOptions}
                    value={getEditMetalSelectValue(selectedProduct)}
                    filterOption={() => true}
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        void searchMetalGroups(inputValue, { target: "edit" });
                      }
                    }}
                    isLoading={metalOptionsLoading}
                    loadingMessage={() => "Searching metal groups..."}
                    noOptionsMessage={() =>
                      modalSearchNoOptionsMessage(
                        metalOptionsLoading,
                        lastEditMetalSearchRef.current.length,
                      )
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setSelectedProduct((prev) => {
                          const currentItemCategory = prev.itemCategoryId
                            ? getEditItemCategorySelectValue(prev)
                            : null;
                          const newProductGroup =
                            generateSuggestedProductGroupName(
                              selectedOption,
                              currentItemCategory,
                            );
                          return {
                            ...prev,
                            metalGroupId: selectedOption.id,
                            metalGroup: selectedOption.groupName,
                            productGroup: newProductGroup,
                            short_code: generateShortCode(newProductGroup),
                          };
                        });
                      } else {
                        setSelectedProduct({
                          ...selectedProduct,
                          metalGroupId: null,
                          metalGroup: "N/A",
                        });
                      }
                    }}
                    getOptionLabel={(option) => option.groupName}
                    getOptionValue={(option) => option.id}
                    isClearable
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label>Item Category</Form.Label>
                  <Select
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    options={editItemOptions}
                    value={getEditItemCategorySelectValue(selectedProduct)}
                    filterOption={() => true}
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        void searchItemCategories(inputValue, { target: "edit" });
                      }
                    }}
                    isLoading={itemOptionsLoading}
                    loadingMessage={() => "Searching item categories..."}
                    noOptionsMessage={() =>
                      modalSearchNoOptionsMessage(
                        itemOptionsLoading,
                        lastEditItemSearchRef.current.length,
                      )
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setSelectedProduct((prev) => {
                          const currentMetalGroup = prev.metalGroupId
                            ? getEditMetalSelectValue(prev)
                            : null;
                          const newProductGroup =
                            generateSuggestedProductGroupName(
                              currentMetalGroup,
                              selectedOption,
                            );
                          return {
                            ...prev,
                            itemCategoryId: selectedOption.id,
                            itemCategory: selectedOption.categoryName,
                            productGroup: newProductGroup,
                            short_code: generateShortCode(newProductGroup),
                          };
                        });
                      } else {
                        setSelectedProduct({
                          ...selectedProduct,
                          itemCategoryId: null,
                          itemCategory: "N/A",
                        });
                      }
                    }}
                    getOptionLabel={(option) => option.categoryName}
                    getOptionValue={(option) => option.id}
                    isClearable
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label>Product Group</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedProduct.productGroup}
                    className={buttonClass}
                    onChange={(e) => {
                      const newProductGroup = e.target.value;
                      setSelectedProduct((prev) => ({
                        ...prev,
                        productGroup: newProductGroup,
                        short_code: generateShortCode(newProductGroup),
                      }));
                    }}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label>Short Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedProduct.short_code}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        short_code: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              {/* <div className="row">
                <div className="col-md-12 mt-3">
                  <Form.Text className="text-muted">
                    Status is not part of the new Product Group API.
                  </Form.Text>
                </div>
              </div> */}
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Product Group Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          resetAddModalSearchState();
        }}
        centered
        contentClassName={cardClass}
      // size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Product Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate="" onSubmit={handleAddProductGroup}>
            <div className="row">
              {/* Metal Group */}
              <div className="col-md-6 mb-3">
                <Form.Label>Metal Group <span className="text-danger">*</span></Form.Label>
                <Select
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  options={addMetalOptions}
                  isSearchable
                  required
                  placeholder="Select Metal Group"
                  value={
                    newMetalGroup
                      ? {
                        id: newMetalGroup.value,
                        groupName: newMetalGroup.label,
                        shortCode: newMetalGroup.shortCode || "",
                        status: 1,
                      }
                      : null
                  }
                  filterOption={() => true}
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      void searchMetalGroups(inputValue, { target: "add" });
                    }
                  }}
                  isLoading={metalOptionsLoading}
                  loadingMessage={() => "Searching metal groups..."}
                  noOptionsMessage={() =>
                    modalSearchNoOptionsMessage(
                      metalOptionsLoading,
                      lastAddMetalSearchRef.current.length,
                    )
                  }
                  onChange={(selectedOption) => {
                    // console.log("Selected smetal group:", selectedOption);
                    if (selectedOption) {
                      setNewMetalGroup({
                        value: selectedOption.id,
                        label: selectedOption.groupName,
                        shortCode: selectedOption.shortCode,
                        originalName: selectedOption.groupName,
                      });
                    } else {
                      setNewMetalGroup(null);
                    }
                  }}
                  getOptionLabel={(option) => option.groupName}
                  getOptionValue={(option) => option.id}
                  isClearable
                />
              </div>

              {/* Item Category */}
              <div className="col-md-6 mb-3">
                <Form.Label>Item Category <span className="text-danger">*</span></Form.Label>
                <Select
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  options={addItemOptions}
                  isSearchable
                  required
                  placeholder="Select Item Category"
                  value={
                    newItemCategory
                      ? {
                        id: newItemCategory.value,
                        categoryName: newItemCategory.label,
                        shortCode: newItemCategory.shortCode || "",
                        status: 1,
                      }
                      : null
                  }
                  filterOption={() => true}
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      void searchItemCategories(inputValue, { target: "add" });
                    }
                  }}
                  isLoading={itemOptionsLoading}
                  loadingMessage={() => "Searching item categories..."}
                  noOptionsMessage={() =>
                    modalSearchNoOptionsMessage(
                      itemOptionsLoading,
                      lastAddItemSearchRef.current.length,
                    )
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setNewItemCategory({
                        value: selectedOption.id,
                        label: selectedOption.categoryName,
                        shortCode: selectedOption.shortCode,
                        originalName: selectedOption.categoryName,
                      });
                    } else {
                      setNewItemCategory(null);
                    }
                  }}
                  getOptionLabel={(option) => option.categoryName}
                  getOptionValue={(option) => option.id}
                  isClearable
                />
              </div>

              {/* Product Group */}
              <div className="col-md-6 mb-3">
                <Form.Label>Product Group <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Product Group"
                  value={newProductName}
                  className={buttonClass}
                  onChange={(e) => setNewProductName(e.target.value)}
                />
              </div>

              {/* Short Code */}
              <div className="col-md-6 mb-3">
                <Form.Label>Short Code <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Short Code"
                  value={newShortCode}
                  className={buttonClass}
                  onChange={(e) => setNewShortCode(e.target.value)}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex flex-wrap gap-3 mt-3 justify-content-end">
              <Button type="submit" variant="primary" className={buttonClass}>
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <OpeningPoolModal
        show={showOpeningPoolModal}
        onHide={() => setShowOpeningPoolModal(false)}
        onSaved={() =>
          fetchProductGroups(currentPage, itemsPerPage, searchQuery)
        }
        buttonClass={buttonClass}
        tableClass={tableClass}
        cardClass={cardClass}
        buttonTableStyle={buttonTableStyle}
      />

      {/* Success Modal */}
    </div>
  );
}

export default ProductGroupTable;
